(() => {
'use strict';
/** Rendering-only identity match. Never authorizes fitment fallback. */
function createVisualizerResolver({fetchImpl=fetch}={}) {
  const normalize=value=>String(value??'').trim().toLowerCase();
  return async function resolve(selection,{signal}={}) {
    if(selection?.provider!=='twg_app'||!selection.trimName)return {state:'unmapped',candidates:[]};
    const candidates=[];const ids=new Set();
    for(let page=1;page<=50;page++){
      const url=new URL('https://api.autosyncstudio.com/vehicles');
      for(const [key,value] of Object.entries({key:'mayhem','f-year':selection.year,'f-make':selection.make,'f-model':selection.model,'p-size':500,'p-number':page}))url.searchParams.set(key,String(value));
      const response=await fetchImpl(url,{signal,headers:{accept:'application/json'}});
      if(!response.ok)throw new Error('Visualizer vehicle lookup unavailable');
      const body=await response.json();
      if(body.Error||!Array.isArray(body.Vehicles))throw new Error('Incomplete visualizer vehicle lookup');
      for(const row of body.Vehicles){
        if(String(row.Year)!==String(selection.year)||normalize(row.Make)!==normalize(selection.make)||normalize(row.Model)!==normalize(selection.model)||normalize(row.Submodel)!==normalize(selection.trimName))continue;
        if(!/^\d{1,12}$/.test(String(row.Id)))throw new Error('Invalid visualizer vehicle identity');
        if(ids.has(String(row.Id)))throw new Error('Duplicate visualizer identity');
        ids.add(String(row.Id));candidates.push({id:String(row.Id),body:String(row.Body||''),bed:String(row.Bed||''),bodyLabel:[row.Body,row.Bed].filter(Boolean).join(' · '),label:[row.Year,row.Make,row.Model,row.Submodel,row.Body,row.Bed].filter(Boolean).join(' · ')});
      }
      if(!body.MoreItems){
        const selected=candidates.find(row=>row.id===String(selection.visualizerVehicleId||'')) || (candidates.length===1?candidates[0]:null);
        return selected?{state:'matched',vehicleId:selected.id,candidates}:{state:candidates.length?'choose_body':'unmapped',candidates};
      }
    }
    throw new Error('Incomplete visualizer pagination');
  };
}

/** TWG's Shopify proxy contract, supplied October 16, 2026.
 * No network traffic until explicitly called; no credentials or automatic fallback.
 */
function createTWGAppClient({fetchImpl=fetch,storage=null,now=Date.now}={}) {
  const cache=new Map(), pending=new Map();
  const prefix='mayhem:fg-choices:v1:';
  function cached(key,loader,signal,persist=false,ttl=persist?21600000:60000) {
    if(signal?.aborted)return Promise.reject(new DOMException('Vehicle changed','AbortError'));
    let saved=cache.get(key);
    if(!saved&&persist)try{saved=JSON.parse(storage?.getItem(prefix+key)||'null');}catch{}
    let request;
    if(saved&&saved.expires>now())request=Promise.resolve(saved.value);
    else {
      request=pending.get(key);
      if(!request){
        const controller=new AbortController(),timer=setTimeout(()=>controller.abort(),15000);
        request=Promise.resolve(loader(controller.signal)).then(value=>{
          const entry={value,expires:now()+ttl};cache.set(key,entry);
          if(cache.size>100)cache.delete(cache.keys().next().value);
          if(persist)try{storage?.setItem(prefix+key,JSON.stringify(entry));}catch{}
          return value;
        }).finally(()=>{clearTimeout(timer);pending.delete(key);});
        pending.set(key,request);
      }
    }
    return new Promise((resolve,reject)=>{
      const abort=()=>reject(new DOMException('Vehicle changed','AbortError'));
      signal?.addEventListener('abort',abort,{once:true});
      request.then(value=>{if(!signal?.aborted)resolve(structuredClone(value));},reject)
        .finally(()=>signal?.removeEventListener('abort',abort));
    });
  }
  const root='/apps/twg-faw/fitment/';
  async function read(route,params={},signal) {
    const search=new URLSearchParams();
    for(const [key,value] of Object.entries(params)) if(value!==undefined && value!==null)search.set(key,String(value));
    const response=await fetchImpl(root+route+(search.size?'?'+search:''),{signal,headers:{Accept:'application/json'},credentials:'same-origin'});
    let body;try{body=await response.json();}catch{throw new Error('Fitment service returned an invalid response');}
    if(!response.ok){
      const error=new Error(response.status===401||response.status===403?'Fitment service access is not enabled':response.status===429?'Too many requests. Please try again shortly.':'Fitment service is unavailable. Please try again.');
      error.status=response.status;throw error;
    }
    if(!body || typeof body!=='object')throw new Error('Invalid fitment response');
    return body;
  }
  function identity(selection) {
    const {year,make,model,submodel}=selection??{};
    if(!/^\d{4}$/.test(String(year))||typeof make!=='string'||!make.trim()||typeof model!=='string'||!model.trim())throw new Error('Select year, make and model');
    if(submodel!==undefined && submodel!==null && submodel!=='' && !/^\d+$/.test(String(submodel)))throw new Error('Invalid trim ID');
    return {year,make,model,...(submodel!==undefined&&submodel!==null&&submodel!==''?{submodel}:{})};
  }
  async function list(route,params,signal){
    return cached(route+'?'+new URLSearchParams(params),async sharedSignal=>{
      const body=await read(route,params,sharedSignal);
      if(!Array.isArray(body.results)||body.results.some(value=>route==='submodels'? !value||!/^\d+$/.test(String(value.id))||typeof value.name!=='string'||!value.name.trim():!['string','number'].includes(typeof value)||!String(value).trim()))throw new Error('Incomplete vehicle list');
      return body.results;
    },signal,true);
  }
  return Object.freeze({
    years:({signal}={})=>list('years',{},signal),
    makes:(year,{signal}={})=>{if(!/^\d{4}$/.test(String(year)))throw new Error('Invalid year');return list('makes',{year},signal);},
    models:(year,make,{signal}={})=>{const q=identity({year,make,model:'_'});return list('models',{year:q.year,make:q.make},signal);},
    submodels:(selection,{signal}={})=>{const {year,make,model}=identity(selection);return list('submodels',{year,make,model},signal);},
    async wheels(selection,{signal}={}) {
      const query=identity(selection);
      return cached('wheels?'+new URLSearchParams(query),async sharedSignal=>{
      const body=await read('wheels',query,sharedSignal);
      if(!Array.isArray(body.results)||!body.vehicle||String(body.vehicle.year)!==String(query.year)||body.vehicle.make!==query.make||body.vehicle.model!==query.model)throw new Error('Vehicle response mismatch or incomplete results');
      const seen=new Set();
      for(const row of body.results){
        if(!row||typeof row.item!=='string'||!row.item.trim()||row.item!==row.item.trim()||seen.has(row.item)||!Array.isArray(row.types)||row.types.some(type=>typeof type!=='string'))throw new Error('Invalid or duplicate wheel result');
        seen.add(row.item);
      }
      return {provider:'twg_find_a_wheel',selection:query,vehicle:body.vehicle,types:body.types??{},results:body.results,
        coverage:'not_declared',conditionsComplete:false};
      },signal,true,900000);
    }
  });
}

/** Use the app classifications as supplied; never infer absent installation notes. */
function twgAppResult(response,sku) {
  const pending={state:'unknown',label:'Fitment not verified',reasons:[],applications:[],sourceLabel:'TWG Find a Wheel'};
  if(response?.provider!=='twg_find_a_wheel'||typeof sku!=='string')return pending;
  const rows=response.results.filter(row=>row.item===sku);
  if(rows.length!==1)return {...pending,diagnosticReasons:['No exact SKU match returned; provider coverage is not declared.']};
  const row=rows[0];
  const trimSelected=response.selection.submodel!==undefined && response.selection.submodel!=='';
  const types=[...row.types];
  const accessoryNotes=typeof row.accessories?.note==='string'&&row.accessories.note.trim()?[row.accessories.note]:[];
  const recognized=types.length>0&&types.every(type=>['Standard','Staggered','Upstep','Minus Size','Lifted'].includes(type));
  // DealerWeb categories are independent, not cumulative installation requirements.
  const liftedOnly=types.length>0&&types.every(type=>type==='Lifted');
  const accessoriesMissing=!row.accessories||typeof row.accessories!=='object';
  const conditional=liftedOnly||row.accessories?.hub_ring_needed===true||accessoryNotes.length>0;
  const state=!trimSelected||!recognized?'unknown':conditional?'caution':'fits';
  return {...pending,state,label:state==='fits'?'Fits':state==='caution'?(liftedOnly?'Fits with lift/level':'Fits with conditions'):!trimSelected?'Select your trim':'Fitment not verified',
    fitmentTypes:types,accessoryNotes,directFit:row.accessories?.direct_fit===true,
    accessoriesAvailable:!accessoriesMissing,
    reasons:[...(liftedOnly?['Fitment type: Lifted']:[]),
      ...(row.accessories?.hub_ring_needed===true&&!accessoryNotes.length?['Hub rings required.']:[]),...accessoryNotes],
    diagnosticReasons:!trimSelected?['Select a trim; this list may combine applications across trims.']:!recognized?['Unrecognized fitment classification.']:[],
    rawApplication:row};
}

/** App-native YMM/trim state. Stale responses cannot overwrite newer selections. */
function createTWGAppSession({client,onChange=()=>{},resolveVisualizer=createVisualizerResolver()}) {
  const keys=['year','make','model','submodel'];
  let state={query:{},choices:{},status:'idle',result:null},generation=0,controller;
  const view=()=>structuredClone(state);
  const emit=()=>onChange(view());
  const applyPreview=choice=>{
    state.query.visualizerVehicleId=choice.id;
    state.query.visualizerBody=choice.body||'';
    state.query.visualizerBed=choice.bed||'';
  };
  async function load(){
    const ticket=++generation;controller?.abort();controller=new AbortController();
    const options={signal:controller.signal},q={...state.query};
    state.status='loading';state.result=null;state.error=null;state.previewError=false;emit();
    try{
      let key,values;
      if(!q.year){key='year';values=await client.years(options);}
      else if(!q.make){key='make';values=await client.makes(q.year,options);}
      else if(!q.model){key='model';values=await client.models(q.year,q.make,options);}
      else if(!q.submodel){key='submodel';values=await client.submodels(q,options);}
      else {
        const result=await client.wheels(q,options);
        if(ticket!==generation)return;
        let preview;
        try{
          const trimName=state.choices.submodel.find(row=>row.id===q.submodel)?.label;
          preview=await resolveVisualizer({...q,provider:'twg_app',trimName},{signal:AbortSignal.any([options.signal,AbortSignal.timeout(10000)])});
        }catch(error){
          if(ticket!==generation)return;
          state.previewError=true;
        }
        if(ticket!==generation)return;
        state.result=result;
        state.preview=preview;
        if(preview?.state==='choose_body'){
          state.choices.visualizerVehicleId=preview.candidates.map(row=>({...row,label:row.bodyLabel||row.label}));
          state.status='select_body';emit();return;
        }
        if(preview?.state==='matched'){
          const choice=preview.candidates.find(row=>row.id===preview.vehicleId);
          if(choice)applyPreview(choice);
        }
      }
      if(ticket!==generation)return;
      if(key){
        if(!Array.isArray(values))throw new Error('Invalid vehicle choices');
        state.choices[key]=values.map(value=>key==='submodel'?{id:String(value.id),label:value.name}:{id:String(value),label:String(value)});
        if(state.choices[key].some(value=>!value.id||!value.label||value.id==='undefined'))throw new Error('Invalid vehicle choices');
      }
      state.status=state.result?'ready':'select';emit();
    }catch(error){if(ticket!==generation)return;state.status='unavailable';state.error=error.message;emit();}
  }
  return {state:view,start:load,retry:load,
    select(key,value){
      if(key==='visualizerVehicleId'){
        const choice=state.choices[key]?.find(row=>row.id===String(value));
        if(!choice||!state.result)throw new Error('Choose a listed vehicle body');
        applyPreview(choice);state.status='ready';emit();return Promise.resolve();
      }
      const index=keys.indexOf(key);if(index<0||!state.choices[key]?.some(option=>option.id===String(value)))throw new Error('Choose a listed vehicle option');
      delete state.query.visualizerVehicleId;delete state.query.visualizerBody;delete state.query.visualizerBed;
      delete state.choices.visualizerVehicleId;delete state.preview;
      state.query[key]=String(value);
      for(const downstream of keys.slice(index+1)){delete state.query[downstream];delete state.choices[downstream];}
      return load();
    },
    clear(){generation++;controller?.abort();state={query:{},choices:{},status:'idle',result:null};emit();},
    destroy(){generation++;controller?.abort();}
  };
}

/** Opt-in panel, never auto-mounted. All provider labels are rendered as text. */
function mountTWGAppSelector({container,client,onApply,onClear,showResults=true}) {
  const doc=container.ownerDocument;
  const node=(tag,text)=>{const element=doc.createElement(tag);if(text!==undefined)element.textContent=text;return element;};
  let disposed=false;
  let previewLabels={};
  try{previewLabels=JSON.parse(doc.getElementById('twg-vehicle-preview-labels')?.textContent||'{}');}catch{}
  const session=createTWGAppSession({client,onChange(state){
    if(disposed)return;
    const fragment=doc.createDocumentFragment();
    const message=state.status==='loading'?'Loading vehicle fitment…':state.status==='unavailable'?'Fitment is unavailable. Please try again.':state.query.model&&!state.choices.submodel?.length&&state.status==='select'?'No trims were returned for this vehicle. Try another vehicle.':'Choose your vehicle and trim.';
    const displayMessage=state.status==='select_body'?previewLabels.choose_body:state.previewError?previewLabels.unavailable:message;
    const status=node('p',displayMessage||message);status.setAttribute('role','status');fragment.append(status);
    for(const [key,label] of Object.entries({year:'Year',make:'Make',model:'Model',submodel:'Trim',visualizerVehicleId:previewLabels.body})){
      if(!state.choices[key]?.length)continue;
      const wrapper=node('label',label+' '),select=node('select');select.setAttribute('aria-label',label);
      const placeholder=node('option',key==='visualizerVehicleId'?previewLabels.select_body:'Select '+label.toLowerCase());placeholder.value='';placeholder.disabled=true;placeholder.selected=!state.query[key];select.append(placeholder);
      for(const choice of state.choices[key]){const option=node('option',choice.label);option.value=choice.id;option.selected=state.query[key]===choice.id;select.append(option);}
      select.addEventListener('change',()=>session.select(key,select.value));wrapper.append(select);fragment.append(wrapper);
    }
    if(state.status==='unavailable'){const retry=node('button','Try again');retry.type='button';retry.onclick=()=>session.retry();fragment.append(retry);}
    const clear=node('button','Clear vehicle');clear.type='button';clear.dataset.twgClearVehicle='';clear.onclick=()=>{onClear?.();if(!disposed){session.clear();session.start();}};fragment.append(clear);
    if(state.result){
      const label=[...['year','make','model','submodel'].map(key=>state.choices[key]?.find(x=>x.id===state.query[key])?.label),state.query.visualizerBody,state.query.visualizerBed].filter(Boolean).join(' · ');
      if(onApply){const apply=node('button','Use this vehicle');apply.type='button';apply.disabled=state.status!=='ready';apply.onclick=()=>onApply({...state.query,trimName:state.choices.submodel.find(x=>x.id===state.query.submodel)?.label},label,structuredClone(state.result));fragment.insertBefore(apply,clear);}
      if(showResults){fragment.append(node('p',state.result.results.length?`${state.result.results.length} wheel options returned`:'No wheel options returned for this selection.'));
        for(const row of state.result.results){const result=twgAppResult(state.result,row.item),article=node('article');article.append(node('strong',row.item),node('p',result.label),node('p',result.fitmentTypes.join(' · ')));for(const note of result.accessoryNotes)article.append(node('p',note));fragment.append(article);}
      }
    }
    container.replaceChildren(fragment);
  }});
  session.start();return {session,destroy(){disposed=true;session.destroy();container.replaceChildren();}};
}

/** Fitment Group controls applications and notes wherever it has coverage. */
function providerDecision({ available, vehicleCovered, skuCovered = true, configurationResolved = true }) {
  if (!available) return { provider: 'fitment_group', state: 'use_previous_valid_release', fallback: false };
  if (!configurationResolved) return { provider: 'fitment_group', state: 'choose_configuration', fallback: false };
  if (!vehicleCovered) return { provider: 'autosync', state: 'fallback_allowed', fallback: true, reason: 'vehicle_not_in_fitment_group' };
  if (!skuCovered) return { provider: 'autosync', state: 'fallback_allowed', fallback: true, reason: 'sku_not_in_fitment_group_catalog' };
  return { provider: 'fitment_group', state: 'authoritative', fallback: false };
}

function mayUseFallback(result) {
  // Empty source notes, unresolved source conditions and no matching application
  // within covered data are not permission to obtain a different answer.
  return result?.provider === 'autosync' && result?.fallback === true &&
    ['vehicle_not_in_fitment_group', 'sku_not_in_fitment_group_catalog'].includes(result.reason);
}


/** Uses the current storefront evaluator; never treats an ACES ID as an Autosync ID. */
function createAutosyncAdapter({ evaluate, key = 'mayhem', fetchImpl = fetch }) {
  if (typeof evaluate !== 'function') throw new Error('Autosync evaluator required');
  async function read(path, filters, signal) {
    const url = new URL(path, 'https://api.autosyncstudio.com');
    url.searchParams.set('key', key);
    for (const [name, value] of Object.entries(filters)) url.searchParams.set(name, value);
    const response = await fetchImpl(url, { signal, headers: { accept: 'application/json' } });
    if (!response.ok) throw new Error('Autosync temporarily unavailable');
    const data = await response.json();
    if (data.Error || data.MoreItems) throw new Error('Incomplete Autosync response');
    return data;
  }
  return async function fallback(query, { signal, reason, vehicleBinding, selection } = {}) {
    if (!mayUseFallback({ provider: 'autosync', fallback: true, reason })) throw new Error('Fallback not authorized');
    if (!vehicleBinding?.confirmed || !vehicleBinding.source ||
        ['baseVehicleId','submodelId','bodyTypeId','driveTypeId','regionId'].some(key=>
          !/^\d{1,12}$/.test(String(query[key] ?? '')) || String(vehicleBinding[key]) !== String(query[key])) ||
        String(vehicleBinding.customNote ?? '') !== String(query.customNote ?? '') ||
        !/^\d{1,12}$/.test(String(vehicleBinding.autosyncVehicleId ?? ''))) {
      throw new Error('Confirmed cross-provider vehicle binding required');
    }
    if (typeof query.sku !== 'string' || !query.sku || query.sku.length > 100 || /[,\r\n]/.test(query.sku)) throw new Error('One exact SKU required');
    const id = String(vehicleBinding.autosyncVehicleId);
    const [vehicles, wheels] = await Promise.all([
      read('/vehicles', { 'f-id': id, 'i-fitments': 'true', 'i-optionalFitments': 'true', 'i-plusSizes': 'true' }, signal),
      read('/wheels', { 'f-pns': query.sku, 'p-size': '500', 'i-specs': 'true' }, signal)
    ]);
    const vehicleRows = (vehicles.Vehicles ?? []).filter(row => String(row.Id) === id);
    const wheelRows = (wheels.Wheels ?? []).filter(row => row.Pn === query.sku);
    if (vehicleRows.length !== 1 || wheelRows.length !== 1) {
      return { provider: 'autosync', state: 'unavailable', results: [], reason: 'exact_vehicle_or_sku_unavailable' };
    }
    const evaluated = evaluate(vehicleRows[0], wheelRows[0], selection ?? null);
    if (!['fits', 'caution', 'unknown', 'nofit'].includes(evaluated?.state)) throw new Error('Invalid Autosync evaluation');
    return { provider: 'autosync', state: 'applications', results: [{ sku: query.sku,
      status: { fits: 'fits', caution: 'conditional', unknown: 'unknown', nofit: 'nofit' }[evaluated.state],
      // Diagnostic reasons remain separate from verbatim provider notes.
      diagnosticReasons: evaluated.reasons ?? [],
      sourceNotes: (evaluated.notes ?? []).filter(note => note.source === 'Autosync').map(note => note.text),
      applications: evaluated.applications ?? [],
      vehicleBinding: { ...vehicleBinding } }] };
  };
}


/** Shared client for Mayhem and other sites. No credentials or inferred notes. */
function createFitmentClient({ baseUrl, brand, fetchImpl = fetch, autosyncFallback }) {
  if (!/^[a-z0-9-]{1,40}$/.test(brand)) throw new Error('Invalid brand');
  const root = new URL(baseUrl);
  if (root.protocol !== 'https:') throw new Error('HTTPS required');
  async function read(route, query, signal) {
    const url = new URL(`/v1/${brand}/${route}`, root);
    for (const [key, value] of Object.entries(query ?? {})) {
      if (value !== undefined && value !== null) url.searchParams.set(key, String(value));
    }
    const response = await fetchImpl(url, { signal, headers: { accept: 'application/json' } });
    const body = await response.json();
    if (!response.ok) {
      const error = new Error(body.error ?? 'fitment_request_failed');
      error.status = response.status;
      throw error; // Transport failures must never turn into Autosync fallback.
    }
    return body;
  }
  return {
    vehicles: (query, { signal } = {}) => read('vehicles', query, signal),
    vehicleOptions: (query, { signal } = {}) => read('vehicle-options', query, signal),
    primaryFitment: (query, { signal } = {}) => read('fitment', query, signal),
    async fitment(query, { signal, vehicleBinding, selection } = {}) {
      const primary = await read('fitment', query, signal);
      const decision = primary.fallbackDecision;
      if (primary.provider !== 'fitment_group' || !mayUseFallback(decision) || !autosyncFallback) return primary;
      const fallback = await autosyncFallback({ ...query }, { signal, reason: decision.reason, vehicleBinding, selection });
      if (!fallback || !Array.isArray(fallback.results)) throw new Error('Invalid Autosync adapter response');
      // Keep complete provider responses separate; never attach fallback notes to FG fits.
      return { ...fallback, provider: 'autosync', fallbackReason: decision.reason };
    }
  };
}

/** A provider-native selector. No Autosync identity is needed to query primary fitment. */
function createVehicleSession({ client, onChange = () => {} }) {
  const order = ['year','makeId','modelId','baseVehicleId','submodelId','bodyTypeId','driveTypeId','regionId','customNote'];
  let query = {}, choices = {}, result = null, generation = 0, controller;
  let status = 'idle', error = null, selectedSku;
  const snapshot = () => JSON.parse(JSON.stringify({ query, choices, result, status, error }));
  const emit = () => onChange(snapshot());
  async function refresh() {
    controller?.abort(); controller = new AbortController();
    const ticket = ++generation;
    const signal = controller.signal;
    status = 'loading'; error = null; result = null; emit();
    try {
      if (!query.baseVehicleId) {
        const response = await client.vehicleOptions(query, { signal });
        if (ticket !== generation) return;
        choices = { year: response.years, makeId: response.makes, modelId: response.models,
          baseVehicleId: response.vehicles.map(v => ({ id: v.baseVehicleId, label: `${v.Year} ${v.MakeName} ${v.ModelName}` })) };
        // One exact provider ID is an unambiguous identity, not a trim/body inference.
        if (response.vehicles.length === 1) query.baseVehicleId = response.vehicles[0].baseVehicleId;
        else { status = response.state; emit(); return; }
      }
      const response = await client.primaryFitment({ ...query, sku: selectedSku }, { signal });
      if (ticket !== generation) return;
      result = response;
      choices = { ...choices, ...response.options,
        customNote: (response.options?.customNote ?? []).map(note => ({ id: note, label: note || 'None of the listed conditions' })) };
      status = response.state; emit();
    } catch (cause) {
      if (ticket !== generation) return;
      status = 'unavailable'; error = cause.message; result = null; emit();
    }
  }
  return {
    snapshot,
    start: () => refresh(),
    async select(field, value) {
      const index = order.indexOf(field);
      if (index < 0 || !choices[field]?.some(choice => choice.id === value)) throw new Error('Select a current provider option');
      query[field] = value;
      for (const next of order.slice(index + 1)) { delete query[next]; delete choices[next]; }
      return refresh();
    },
    async checkSku(sku) {
      if (!query.baseVehicleId || typeof sku !== 'string' || !sku.trim()) throw new Error('Vehicle and exact SKU required');
      selectedSku = sku;
      return refresh();
    },
    async loadMore() {
      if (status === 'loading' || result?.nextOffset == null) return;
      const previous = result, ticket = generation;
      status = 'loading'; emit();
      try {
        const page = await client.primaryFitment({ ...query, sku: selectedSku,
          offset: previous.nextOffset, release: previous.release }, { signal: controller.signal });
        if (ticket !== generation) return;
        if (page.release !== previous.release || page.state !== 'applications') throw new Error('Fitment data changed; select your vehicle again');
        result = { ...page, results: [...previous.results, ...page.results] };
        status = page.state; emit();
      } catch (cause) {
        if (ticket !== generation) return;
        result = null; status = 'unavailable'; error = cause.message; emit();
      }
    },
    clear() {
      generation++; controller?.abort(); query = {}; choices = {}; result = null; selectedSku = undefined;
      status = 'idle'; error = null; emit();
    }
  };
}

/** Presentation preserves application conditions and source notes, without invented advice. */
function fitmentDisplay(response) {
  const pending = { choose_configuration: 'Choose your vehicle configuration',
    unmapped_vehicle_code: 'Vehicle details need confirmation',
    no_supported_application: 'No matching application in the fitment data',
    sku_not_in_storefront: 'This wheel is not in the active catalog',
    no_fitment_group_vehicle_coverage: 'Additional fitment lookup required' };
  if (response.state !== 'applications') return { title: pending[response.state] || 'Fitment unavailable', wheels: [] };
  return { title: 'Fitment results', wheels: response.results.map(row => ({
    sku: row.sku,
    applications: response.provider === 'autosync' ? [{
      label: 'Fitment not verified',
      notes: row.sourceNotes ?? [], diagnostics: row.diagnosticReasons ?? []
    }] : (row.applications ?? []).map(app => ({
      label: app.status === 'fits' ? 'Fits' : app.status === 'conditional' && (app.sourceNotes ?? []).length ? 'Fits with conditions' : 'Fitment not verified',
      fitmentType: app.fitmentType, position: app.position, quantity: app.quantity,
      notes: app.sourceNotes ?? [], diagnostics: app.unresolved ?? []
    }))
  })) };
}

/** Opt-in draft panel. Provider strings are text, never HTML. */
function mountFitmentSelector({ container, client, sku, onApply, onClear, showResults = true }) {
  const doc = container.ownerDocument;
  const fields = {year:'Year',makeId:'Make',modelId:'Model',baseVehicleId:'Vehicle',
    submodelId:'Submodel',bodyTypeId:'Body',driveTypeId:'Drive',regionId:'Region',customNote:'Vehicle condition'};
  const node = (tag, text) => {const el=doc.createElement(tag);if(text !== undefined)el.textContent=text;return el;};
  let disposed = false;
  const session = createVehicleSession({client,onChange(state) {
    if (disposed) return;
    const fragment=doc.createDocumentFragment();
    if(showResults) fragment.append(node('h3','Select your vehicle'));
    const status=node('p',state.status==='loading'?'Loading fitment…':state.status==='unavailable'?'Fitment is temporarily unavailable. Please try again.':'Choose the vehicle details that apply.');
    status.setAttribute('role','status');fragment.append(status);
    for(const [key,label] of Object.entries(fields)) {
      const options=state.choices[key];
      if(!options?.length || (key==='baseVehicleId' && options.length===1))continue;
      const wrapper=node('label',label+' '), select=node('select');
      const placeholder=node('option','Select '+label.toLowerCase());placeholder.value='';placeholder.disabled=true;
      placeholder.selected=state.query[key]===undefined;select.append(placeholder);
      options.forEach(choice=>{const option=node('option',choice.label??'Unmapped option');option.value=choice.id;
        option.selected=state.query[key]===choice.id;option.disabled=choice.label==null;select.append(option);});
      select.disabled=state.status==='loading';
      select.addEventListener('change',()=>session.select(key,select.value));wrapper.append(select);fragment.append(wrapper);
    }
    const reset=node('button','Clear vehicle');reset.type='button';reset.dataset.twgClearVehicle='';reset.addEventListener('click',()=>{onClear?.();if(disposed)return;session.clear();session.start();});fragment.append(reset);
    if(sku && state.query.baseVehicleId) {
      const check=node('button','Check this wheel');check.type='button';check.disabled=state.status==='loading';
      check.addEventListener('click',()=>session.checkSku(sku));fragment.append(check);
    }
    if(state.result) {
      if(onApply && ['baseVehicleId','submodelId','bodyTypeId','driveTypeId','regionId'].every(key=>state.query[key]) &&
          ['applications','no_supported_application'].includes(state.result.state) && state.status!=='loading') {
        const apply=node('button','Use this vehicle');apply.type='button';
        apply.addEventListener('click',()=>onApply(state.query,['year','makeId','modelId','submodelId','bodyTypeId','driveTypeId']
          .map(key=>state.choices[key]?.find(option=>option.id===state.query[key])?.label).filter(Boolean).join(' · ')));
        fragment.insertBefore(apply,reset);
      }
      if(showResults) {
      const view=fitmentDisplay(state.result);fragment.append(node('h4',view.title));
      for(const wheel of view.wheels) {
        const article=node('article');article.append(node('strong',wheel.sku));
        for(const app of wheel.applications) {
          article.append(node('p',[app.label,app.fitmentType,app.position].filter(Boolean).join(' · ')));
          if(app.notes.length) {const list=node('ul');app.notes.forEach(note=>list.append(node('li',note)));article.append(list);}
        }
        fragment.append(article);
      }
      if(state.result.nextOffset!=null) {
        fragment.append(node('p',`${state.result.results.length} of ${state.result.total} wheels shown`));
        const more=node('button','Show more');more.type='button';more.disabled=state.status==='loading';
        more.addEventListener('click',()=>session.loadMore());fragment.append(more);
      }
      }
    }
    container.replaceChildren(fragment);
  }});
  session.start();
  return {session,destroy(){disposed=true;session.clear();container.replaceChildren();}};
}

/** Adapt exact application results to the existing Mayhem row states. */
function storefrontResult(response, sku) {
  if(response.provider==='twg_find_a_wheel')return twgAppResult(response,sku);
  const unknown = label => ({state:'unknown',label,reasons:[],applications:[]});
  if(response.provider==='autosync') return unknown('Fitment not verified');
  if (response.provider !== 'fitment_group') return unknown('Fitment not verified');
  if (response.state === 'choose_configuration') return unknown('Choose vehicle details');
  if (response.fallbackDecision?.fallback) return unknown('Additional fitment lookup required');
  const matches = (response.results ?? []).filter(row => row.sku === sku);
  if (matches.length !== 1) return unknown('No confirmed fitment application');
  const applications = matches[0].applications ?? [];
  const best = applications.some(a=>a.status==='fits') ? 'fits' : applications.some(a=>a.status==='conditional' && (a.sourceNotes ?? []).length) ? 'conditional' : 'unknown';
  const selected = applications.filter(a=>a.status===best);
  const positions = [...new Set(selected.map(a=>a.position).filter(Boolean))];
  const label = best==='fits'?'Fits':best==='conditional'?'Fits with conditions':'Fitment not verified';
  return { state: best==='conditional'?'caution':best,
    label: label+(positions.length?' — '+positions.join(' / '):''),
    reasons:[...new Set(selected.flatMap(a=>a.sourceNotes ?? []))], applications };
}

function cleanVehicle(query, label) {
  const clean = {};
  if(query?.provider==='twg_app') {
    if(!/^\d{4}$/.test(String(query.year)) || !/^\d{1,12}$/.test(String(query.submodel)) ||
      !['make','model'].every(key=>typeof query[key]==='string' && query[key].trim() && query[key].length<=150)) throw new Error('Complete app vehicle required');
    return {query:{provider:'twg_app',year:String(query.year),make:query.make,model:query.model,submodel:String(query.submodel),...(typeof query.trimName==='string'&&query.trimName.trim()?{trimName:query.trimName}:{} ),...(/^\d{1,12}$/.test(String(query.visualizerVehicleId||''))?{visualizerVehicleId:String(query.visualizerVehicleId),visualizerBody:String(query.visualizerBody||'').slice(0,150),visualizerBed:String(query.visualizerBed||'').slice(0,150)}:{})},label:String(label||'Selected vehicle')};
  }
  for (const key of ['baseVehicleId','submodelId','bodyTypeId','driveTypeId','regionId']) {
    if (!/^\d{1,12}$/.test(String(query?.[key] ?? ''))) throw new Error('Complete vehicle configuration required');
    clean[key] = String(query[key]);
  }
  if (query.customNote !== undefined) clean.customNote = String(query.customNote);
  return {query:clean,label:String(label || 'Selected vehicle')};
}

/** Persist selections only, never fitment results. Isolate brands/environments. */
function createVehicleStore(storage, scope, now = Date.now) {
  const key = 'twg:mayhem:vehicle:v1:' + scope;
  return {
    key,
    read() {
      try {
        const saved = JSON.parse(storage?.getItem(key) || 'null');
        if (!saved || saved.version !== 1 || !Number.isFinite(saved.expires) || saved.expires <= now()) return null;
        return cleanVehicle(saved.query,saved.label);
      } catch { return null; }
    },
    save(vehicle) {
      try { storage?.setItem(key,JSON.stringify({...vehicle,version:1,expires:now()+86400000})); } catch { /* in-memory selection still works */ }
    },
    clear() { try {storage?.removeItem(key);} catch {} }
  };
}

window.TWGFitmentModules = Object.freeze({createVisualizerResolver,createTWGAppSession,mountTWGAppSelector,createTWGAppClient,twgAppResult,createFitmentClient,createAutosyncAdapter,createVehicleSession,fitmentDisplay,mountFitmentSelector,storefrontResult,cleanVehicle,createVehicleStore});
})();
